import { useRef, useState } from "react";
import { createFilterProduct, updateFilterProduct, deleteFilterProduct } from "../../auth/apiProduct";
import { elakayAutoText } from "../../pageInside/dataElakay";
import useSellerFilterDefinitions from "./useSellerFilterDefinitions";
import useSellerCombinationRows, { sellerCategoryId, validateSellerCombinations } from "./useSellerCombinationRows";

const EMPTY_FILTER_PRODUCTS = [];

// Shared Seller workflow: the creation wizard and product editor save the same
// filterProduct documents using their original IDs and per-operation snapshots.
export default function useSellerFilterEditor({
  productId, categoryId, filtersCategory, filterProducts = EMPTY_FILTER_PRODUCTS,
  infoSource, token, requiresDropshippingConfirmation = false, createExtras = {}, onSaved,
}) {
    const currentFilterCategoryId = sellerCategoryId(categoryId);
    const combinationEditor = useSellerCombinationRows({
      productId,
      filterProducts,
      categoryId: currentFilterCategoryId,
    });
    const combinationRows = combinationEditor.rows;
    const [filterComboError, setFilterComboError] = useState("");
    const [filterComboMessage, setFilterComboMessage] = useState("");
    const [savingFilterCombinations, setSavingFilterCombinations] = useState(false);
    const [filterSaveNeedsReload, setFilterSaveNeedsReload] = useState(false);
    const savingFilterCombinationsRef = useRef(false);
    const {
      filterDefs, activeDefs, sellerSelectedValues, onToggleFilterValue, onToggleAllFilterValues,
    } = useSellerFilterDefinitions({
      filtersCategory,
      rows: combinationRows,
      categoryId: currentFilterCategoryId,
      productId,
      infoSource,
      onError: setFilterComboError,
    });
    const { duplicateRowIds, incompleteRowIds } = validateSellerCombinations(combinationRows, activeDefs);
    const clearFilterFeedback = () => {
      setFilterComboError("");
      setFilterComboMessage("");
    };
    const onCombinationFieldChange = (rowId, defKey, value) => {
      clearFilterFeedback();
      combinationEditor.changeField(rowId, defKey, value);
    };
    const onCombinationQuantityChange = (rowId, value) => {
      clearFilterFeedback();
      combinationEditor.patchRow(rowId, { quantity: value });
    };
    const onCombinationPriceChange = (rowId, value) => {
      clearFilterFeedback();
      combinationEditor.patchRow(rowId, { priceFil: value });
    };
    const onAddCombinationRow = () => {
      clearFilterFeedback();
      combinationEditor.addRow();
    };
    const onRemoveCombinationRow = (rowId) => {
      clearFilterFeedback();
      combinationEditor.removeRow(rowId);
    };

    const saveFilterCombinations = async ({ requireCombination = false } = {}) => {
      if (savingFilterCombinationsRef.current || filterSaveNeedsReload || !currentFilterCategoryId || !productId) return false;
      clearFilterFeedback();
      if (requireCombination && !combinationRows.length) {
        setFilterComboError(filterDefs.length === 0
          ? elakayAutoText("h_seller_stock_row_required", "Add one stock row before continuing to images.")
          : elakayAutoText("h_seller_combination_required", "Add at least one filter combination before continuing to images."));
        return false;
      }
      if (duplicateRowIds.size > 0) {
        setFilterComboError(elakayAutoText("h_seller_duplicate_combination", "This filter combination already exists."));
        return false;
      }
      if (incompleteRowIds.size > 0) {
        setFilterComboError(elakayAutoText("h_seller_invalid_combinations", "Choose an allowed value for every filter, a whole quantity of 0 or more, and a valid non-negative price."));
        return false;
      }
      let savedRows = combinationRows.map((row) => ({
        ...(filterProducts.find((fp) => String(fp._id) === row.id) || {}),
        _id: row.id, catID: row.catID, filter: { ...row.filter }, quantity: Number(row.quantity), priceFil: row.priceFil ?? "",
      }));
      const { creates, updates, deletes } = combinationEditor.changes;
      if (!creates.length && !updates.length && !deletes.length) {
        setFilterComboMessage(elakayAutoText("h_seller_no_filter_changes", "No filter changes to save."));
        return savedRows;
      }
      savingFilterCombinationsRef.current = true;
      setSavingFilterCombinations(true);
      const requireSuccess = (response) => {
        if (!response || response.error || response.success === false) {
          throw new Error(typeof response?.error === "string" ? response.error : elakayAutoText("h_seller_filter_save_error", "Could not save filter changes. Your remaining changes are still here; try again."));
        }
        return response;
      };
      const payloadFor = (row) => ({ filter: { ...row.filter }, quantity: Number(row.quantity), priceFil: row.priceFil ?? "" });
      try {
        // Serialize mutations and acknowledge each successful operation. An
        // error leaves the unsaved rows editable and a retry skips successes.
        for (const row of updates) {
          requireSuccess(await updateFilterProduct(token, productId, row.id, payloadFor(row), requiresDropshippingConfirmation));
          combinationEditor.acknowledge(row.id, { ...row, quantity: Number(row.quantity) });
        }
        for (const row of creates) {
          const response = requireSuccess(await createFilterProduct(token, productId, { ...createExtras, ...payloadFor(row), catID: row.catID }));
          const savedId = response.filterProduct?._id;
          if (!savedId) {
            setFilterSaveNeedsReload(true);
            throw new Error(elakayAutoText("h_seller_filter_missing_saved_id", "The saved combination ID is missing. Reload the product before trying again."));
          }
          savedRows = savedRows.map((saved) => saved._id === row.id ? { ...saved, ...response.filterProduct, _id: String(savedId) } : saved);
          combinationEditor.acknowledge(row.id, { ...row, id: String(savedId), isNew: false, quantity: Number(row.quantity) });
        }
        for (const row of deletes) {
          requireSuccess(await deleteFilterProduct(token, productId, row.id, requiresDropshippingConfirmation));
          combinationEditor.acknowledge(row.id, null);
        }
        setFilterComboMessage(elakayAutoText("h_seller_filter_changes_saved", "Filter changes saved."));
        onSaved?.(savedRows);
        return savedRows;
      } catch (err) {
        setFilterComboError(err?.message || elakayAutoText("h_seller_filter_save_error", "Could not save filter changes. Your remaining changes are still here; try again."));
        return false;
      } finally {
        savingFilterCombinationsRef.current = false;
        setSavingFilterCombinations(false);
      }
    };

    return {
      combinationRows, filterDefs, activeDefs, sellerSelectedValues,
      onToggleFilterValue, onToggleAllFilterValues,
      onCombinationFieldChange, onCombinationQuantityChange, onCombinationPriceChange,
      onAddCombinationRow, onRemoveCombinationRow, duplicateRowIds, incompleteRowIds,
      filterComboError, filterComboMessage, savingFilterCombinations, filterSaveNeedsReload,
      saveFilterCombinations, currentFilterCategoryId,
      canSave: Boolean(productId && currentFilterCategoryId),
    };
}
